import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Key, Mail, ArrowLeft, Loader2 } from "lucide-react";
import { getApiUrl } from "../config/api";

export default function RequestReset() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setError("");
    setStatus("");

    try {
      await axios.post(getApiUrl("/api/auth/request-reset"), { email });
      setStatus("Check your email for a password reset link. If you don't see it, check your spam folder.");
    } catch (err) {
      console.error("Password reset request error:", err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 404) {
        setError("No account found with that email address");
      } else {
        setError("Failed to send reset link. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      {/* Custom logo in upper left corner */}
      <div className="absolute top-8 left-8 flex items-center space-x-3 z-10">
        <img 
          src="/MyTrailerLogo.png" 
          alt="MyTrailer Logo" 
          className="h-14 w-auto drop-shadow-sm"
        />
      </div>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#51380d]/5 to-[#3d2a0a]/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-[#51380d]/5 to-[#3d2a0a]/5 rounded-full blur-3xl"></div>
      </div>

      {/* Centered form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Request Reset Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#b07135] rounded-2xl mb-4 shadow-lg">
                <Key className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
              <p className="text-gray-600">Enter your email to receive a reset link</p>
            </div>
            
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Success Message */}
            {status && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium">{status}</span>
              </div>
            )}
            
            {/* Reset Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="relative">
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                    emailFocused ? 'text-[#51380d]' : 'text-gray-400'
                  }`} />
                  <input
                    className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-[#51380d]/20 ${
                      emailFocused 
                        ? 'border-[#51380d] bg-white' 
                        : 'border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300'
                    } ${error ? 'border-red-300 bg-red-50' : ''}`}
                    placeholder="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-[#51380d]/30 ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed shadow-none"
                    : "bg-[#b07135] hover:shadow-lg hover:shadow-[#51380d]/25"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Sending Reset Link...</span>
                  </div>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
            
            {/* Additional Links */}
            <div className="mt-8 space-y-4 text-center">
              <button
                onClick={() => navigate("/login")}
                className="text-sm text-gray-600 hover:text-[#51380d] transition-colors duration-200 font-medium flex items-center justify-center mx-auto"
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sign In
              </button>
              
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Don't have an account?</p>
                <button
                  onClick={() => navigate("/signup")}
                  className="text-sm font-semibold text-[#51380d] hover:text-[#3d2a0a] transition-colors duration-200"
                  disabled={isLoading}
                >
                  Create an account
                </button>
              </div>
              
              <div className="pt-2">
                <a
                  href="/privacy"
                  className="text-xs text-gray-500 hover:text-[#51380d] transition-colors duration-200"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
